import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { unitAPI } from '../services/api';
import API_BASE_URL from '../services/apiBaseUrl';
import websocketService from '../services/websocket';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuthStore } from '../store/authStore';
import { canManageUnitImport } from '../utils/unitImportPermissions';
import { hasAdminPermission } from '../utils/adminPermissions';
import { formatLocalizedDateTime, normalizeAutogeneratedDeviceName } from '../utils/i18nFormatters';
import { formatStationDisplayId } from '../utils/stationId';
import { buildUnitPartyBindingPayload } from '../utils/unitPartyBindings';
import { downloadUnitWaterWorkbook } from '../utils/unitWaterExport';
import { buildUnitOutletQrCodes } from '../utils/unitQrCode';
import {
  DEFAULT_WATER_PRICES,
  WATER_PRICE_LIMITS,
  isWaterPriceWithinLimits
} from '../utils/waterPriceLimits';
import {
  getUnitPartnerDisplay,
  getUnitRegionalPartnerDisplay,
  getUnitStewardDisplay
} from '../utils/unitParties';
import {
  getPresenceLastDataAt,
  getUnitConnectionStatus,
  mergeDevicePresenceEvent,
  mergePolledUnits
} from '../utils/devicePresenceMerge';
import {
  Monitor,
  AlertTriangle,
  Search,
  Wifi,
  WifiOff,
  Upload,
  X,
  Loader2,
  CheckCircle2,
  Save,
  Edit,
  CalendarDays,
  MapPin,
  Wand2,
  FileSpreadsheet,
  QrCode,
  Download
} from 'lucide-react';
import { AdminModalSurface } from './AdminModal';
import RoleUserSearchField from './RoleUserSearchField';

const UNIT_STATUS_REFRESH_MS = Number(process.env.REACT_APP_UNIT_STATUS_REFRESH_MS || 60000);

const getUnitOpenAlertCount = (unit) => Number(unit?.openAlertCount || unit?.alertSummary?.openCount || 0);
const getUnitLatestOpenAlert = (unit) => unit?.latestOpenAlert || unit?.alertSummary?.latestOpenAlert || null;
const hasUnitOpenAlert = (unit) => Boolean(unit?.hasOpenAlert || getUnitOpenAlertCount(unit) > 0);
const formatPartySearchQuery = (party, id) => (
  party?.name ? `${party.name}${id ? ` (ID: ${id})` : ''}` : ''
);

const formatInspectionDateTime = (value, language) => {
  if (!value) {
    return null;
  }

  const formatted = formatLocalizedDateTime(value, language);
  return formatted === '-' ? null : formatted;
};

const MINERAL_WATER_PRICE_LIMITS = WATER_PRICE_LIMITS.mineral;
const PURE_WATER_PRICE_LIMITS = WATER_PRICE_LIMITS.pure;
const INDONESIA_PROVINCES = [
  'Aceh',
  'Sumatera Utara',
  'Sumatera Barat',
  'Riau',
  'Kepulauan Riau',
  'Jambi',
  'Sumatera Selatan',
  'Bangka Belitung',
  'Bengkulu',
  'Lampung',
  'DKI Jakarta',
  'Jakarta',
  'Banten',
  'Jawa Barat',
  'Jawa Tengah',
  'DI Yogyakarta',
  'Yogyakarta',
  'Jawa Timur',
  'Bali',
  'Nusa Tenggara Barat',
  'Nusa Tenggara Timur',
  'Kalimantan Barat',
  'Kalimantan Tengah',
  'Kalimantan Selatan',
  'Kalimantan Timur',
  'Kalimantan Utara',
  'Sulawesi Utara',
  'Gorontalo',
  'Sulawesi Tengah',
  'Sulawesi Barat',
  'Sulawesi Selatan',
  'Sulawesi Tenggara',
  'Maluku',
  'Maluku Utara',
  'Papua',
  'Papua Barat',
  'Papua Tengah',
  'Papua Pegunungan',
  'Papua Selatan',
  'Papua Barat Daya'
];

const normalizeAddressText = (value) => String(value || '').replace(/\s+/g, ' ').trim();
const normalizeAddressKey = (value) => normalizeAddressText(value)
  .toLowerCase()
  .replace(/[.,]/g, '')
  .replace(/\bprovinsi\b|\bprovince\b|\bprov\b/g, '')
  .replace(/\bkota\b|\bkabupaten\b|\bkab\b|\bkec\b|\bkecamatan\b|\bkel\b|\bkelurahan\b|\bdesa\b/g, '')
  .trim();

const stripAddressPrefix = (value) => normalizeAddressText(value)
  .replace(/\b\d{5}\b/g, '')
  .replace(/^(provinsi|province|prov\.?)\s+/i, '')
  .replace(/^(kota|kabupaten|kab\.?)\s+/i, '')
  .replace(/^(kecamatan|kec\.?|kelurahan|kel\.?|desa)\s+/i, '')
  .trim();

const detectProvince = (token) => {
  const key = normalizeAddressKey(token);
  const isJakartaCity = /\bjakarta\s+(selatan|barat|timur|utara|pusat)\b/i.test(key);
  const province = INDONESIA_PROVINCES.find((item) => {
    const provinceKey = normalizeAddressKey(item);
    if (provinceKey === 'jakarta') {
      return !isJakartaCity && (
        key === provinceKey ||
        key.includes('dki jakarta') ||
        key.includes('daerah khusus ibukota jakarta')
      );
    }
    return key === provinceKey || key.includes(provinceKey);
  });

  if (!province) return '';
  return province === 'Jakarta' ? 'DKI Jakarta' : province;
};

const splitIndonesianAddress = (fullAddress) => {
  const chunks = String(fullAddress || '')
    .split(/[\n,;|]+/)
    .map(normalizeAddressText)
    .filter(Boolean);

  const result = {
    provinceName: '',
    cityName: '',
    districtName: '',
    addressDetail: ''
  };

  if (chunks.length === 0) {
    return result;
  }

  const usedIndexes = new Set();
  const provinceIndex = chunks.findIndex((chunk) => detectProvince(chunk));
  if (provinceIndex >= 0) {
    result.provinceName = detectProvince(chunks[provinceIndex]);
    usedIndexes.add(provinceIndex);
  }

  let cityIndex = chunks.findIndex((chunk, index) => (
    !usedIndexes.has(index) && /^(kota|kabupaten|kab\.)\s+/i.test(chunk)
  ));
  if (cityIndex < 0 && provinceIndex > 0) {
    cityIndex = provinceIndex - 1;
  }
  if (cityIndex >= 0 && !usedIndexes.has(cityIndex)) {
    result.cityName = stripAddressPrefix(chunks[cityIndex]);
    usedIndexes.add(cityIndex);
  }

  let districtIndex = chunks.findIndex((chunk, index) => (
    !usedIndexes.has(index) && /^(kecamatan|kec\.?|kelurahan|kel\.?|desa)\s+/i.test(chunk)
  ));
  if (districtIndex < 0 && cityIndex > 0) {
    districtIndex = cityIndex - 1;
  }
  if (districtIndex >= 0 && !usedIndexes.has(districtIndex)) {
    result.districtName = stripAddressPrefix(chunks[districtIndex]);
    usedIndexes.add(districtIndex);
  }

  result.addressDetail = chunks
    .filter((_, index) => !usedIndexes.has(index))
    .join(', ');

  return result;
};

const formatCompactStationAddress = (unit = {}) => {
  const structured = [unit.districtName, unit.cityName]
    .map(stripAddressPrefix)
    .filter(Boolean);
  if (structured.length > 0) return structured.slice(0, 2).join(' · ');

  const fallback = normalizeAddressText(unit.location || unit.addressDetail || unit.provinceName);
  if (!fallback) return '';
  return fallback.length > 42 ? `${fallback.slice(0, 39).trim()}...` : fallback;
};

const getFullStationAddress = (unit = {}) => normalizeAddressText(
  unit.location || [unit.addressDetail, unit.districtName, unit.cityName, unit.provinceName].filter(Boolean).join(', ')
);

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const getPart = (type) => parts.find((part) => part.type === type)?.value;
  const year = getPart('year');
  const month = getPart('month');
  const day = getPart('day');

  return year && month && day ? `${year}-${month}-${day}` : '';
};

export default function UnitControl() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const canEditUnits = canManageUnitImport(user);
  const canManageUnitPricing = hasAdminPermission(user, 'manage_unit_pricing');
  const canOpenUnitEditor = canEditUnits || canManageUnitPricing;
  const canManagePartnerBindings = hasAdminPermission(user, 'manage_partner_bindings');
  const canExportUnitWater = hasAdminPermission(user, 'view_finance');
  const canViewUnitQr = hasAdminPermission(user, 'view_units');
  const defaultEditForm = {
    rpOwnerId: '',
    puskudId: '',
    stewardId: '',
    installedAt: '',
    simCardNumber: '',
    simPaidUntil: '',
    location: '',
    provinceCode: '',
    provinceName: '',
    cityName: '',
    districtName: '',
    addressDetail: '',
    mineralWaterPrice: '',
    pureWaterPrice: ''
  };
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchProps] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [regionalPartnerFilter, setRegionalPartnerFilter] = useState('all');
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importDeviceNo, setImportDeviceNo] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState(null);

  // Edit unit detail dialog.
  const [showEditUnitDialog, setShowEditUnitDialog] = useState(false);
  const [selectedUnitForEdit, setSelectedUnitForEdit] = useState(null);
  const [editForm, setEditForm] = useState(defaultEditForm);
  const [editLoading, setEditLoading] = useState(false);
  const jakartaNow = new Date(Date.now() + (7 * 60 * 60 * 1000));
  const [exportUnit, setExportUnit] = useState(null);
  const [exportYear, setExportYear] = useState(jakartaNow.getUTCFullYear());
  const [exportMonth, setExportMonth] = useState(jakartaNow.getUTCMonth() + 1);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');
  const [qrUnit, setQrUnit] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');
  const formatWaterPrice = (value) => {
    const numericValue = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(numericValue)) {
      return t('units.table.priceNotSet');
    }

    return `Rp ${numericValue.toFixed(0)}/L`;
  };

  const formatStationId = (unit) => formatStationDisplayId(unit);

  const formatListDeviceName = (unit) => {
    const stationId = formatStationId(unit);
    const displayName = normalizeAutogeneratedDeviceName(unit.deviceName, stationId);
    const fullDeviceId = String(unit.deviceId || '').trim();
    if (!displayName || displayName === stationId || displayName === fullDeviceId || /^\d{7,}$/.test(displayName)) {
      return '';
    }
    return displayName;
  };

  const getUnitPartner = getUnitPartnerDisplay;
  const getUnitRegionalPartner = getUnitRegionalPartnerDisplay;
  const getUnitSteward = getUnitStewardDisplay;

  const renderPartyCell = (party) => (
    <div className="min-w-[120px]">
      <div className={`text-xs font-semibold ${party?.name || party?.phone ? 'text-slate-200' : 'text-slate-500'}`}>
        {party?.name || party?.phone || t('units.table.notAssigned')}
      </div>
      {party?.name && party?.phone && (
        <div className="mt-1 text-[10px] text-slate-500">{party.phone}</div>
      )}
    </div>
  );

  const buildUnitSearchText = (unit) => [
    unit.id,
    formatStationId(unit),
    unit.deviceId,
    unit.deviceName,
    unit.stationId,
    unit.unitCode,
    unit.deviceNo,
    unit.rpOwnerId,
    unit.rpOwner?.name,
    unit.rpOwner?.phoneNumber,
    unit.rpOwner?.phone,
    unit.partner?.name,
    unit.partner?.phoneNumber,
    unit.partner?.phone,
    unit.kudOwner?.name,
    unit.kudOwner?.phoneNumber,
    unit.kudOwner?.phone,
    unit.partnerName,
    unit.kudOwnerName,
    unit.puskudId,
    unit.puskud?.name,
    unit.puskud?.phoneNumber,
    unit.puskud?.phone,
    unit.regionalPartner?.name,
    unit.regionalPartner?.phoneNumber,
    unit.regionalPartner?.phone,
    unit.puskudOwner?.name,
    unit.puskudOwner?.phoneNumber,
    unit.puskudOwner?.phone,
    unit.puskudName,
    unit.regionalPartnerName,
    unit.puskudOwnerName,
    unit.stewardId,
    unit.steward?.name,
    unit.steward?.phone,
    unit.steward?.phoneNumber,
    unit.location,
    unit.provinceName,
    unit.cityName,
    unit.districtName,
    unit.addressDetail,
    unit.installedAt
  ].map((value) => String(value || '').toLowerCase()).join(' ');

  const formatLastHeartbeat = (value) => {
    if (!value) {
      return t('unitControl.lastSeenNever', { defaultValue: 'No heartbeat yet' });
    }

    const heartbeatDate = new Date(value);
    if (Number.isNaN(heartbeatDate.getTime())) {
      return t('unitControl.lastSeenUnknown', { defaultValue: 'Last seen unknown' });
    }

    const diffMs = Date.now() - heartbeatDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) {
      return t('unitControl.lastSeenJustNow', { defaultValue: 'Just now' });
    }

    if (diffMinutes < 60) {
      return t('unitControl.lastSeenMinutes', {
        count: diffMinutes,
        defaultValue: `${diffMinutes}m ago`
      });
    }

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return t('unitControl.lastSeenHours', {
        count: diffHours,
        defaultValue: `${diffHours}h ago`
      });
    }

    const diffDays = Math.floor(diffHours / 24);
    return t('unitControl.lastSeenDays', {
      count: diffDays,
      defaultValue: `${diffDays}d ago`
    });
  };

  const validateUnitPrice = (value, waterTypeLabel, limits) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return t('unitControl.priceMustBeNumber', { waterType: waterTypeLabel });
    }

    if (!isWaterPriceWithinLimits(numericValue, limits)) {
      return t('unitControl.priceRangeError', {
        waterType: waterTypeLabel,
        min: limits.min,
        max: limits.max
      });
    }

    return null;
  };

  const normalizePriceInput = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? String(numericValue) : String(value).trim();
  };

  const handleDeviceUpdate = useCallback((data) => {
    console.log('[UnitControl] Device update received:', data);
    setUnits(prevUnits => {
      const deviceId = data?.deviceId || data?.data?.deviceId;
      if (!deviceId) return prevUnits;
      const current = prevUnits.find((unit) => unit.deviceId === deviceId);
      const sameRevision = current
        && current.version === data.data?.version
        && current.detectedAt === data.data?.detectedAt
        && current.presenceState === data.data?.presenceState;
      const merged = mergeDevicePresenceEvent(prevUnits, data);
      if (sameRevision) return merged;
      return merged.map((unit) => (
        unit.deviceId === deviceId ? { ...unit, _justUpdated: true } : unit
      ));
    });

    const deviceId = data?.deviceId || data?.data?.deviceId;
    if (deviceId) {
      setTimeout(() => {
        setUnits(units => units.map(u => ({
          ...u,
          _justUpdated: u.deviceId === deviceId ? false : u._justUpdated
        })));
      }, 500);
    }
  }, []);

  // 澶勭悊WebSocket杩炴帴鐘舵€?
  const handleWsConnected = useCallback(() => {
    setWsStatus('connected');
    console.log('[UnitControl] WebSocket connected');
  }, []);

  const handleWsDisconnected = useCallback(() => {
    setWsStatus('disconnected');
    console.log('[UnitControl] WebSocket disconnected');
  }, []);

  // 鍒濆鍖朩ebSocket鐩戝惉
  useEffect(() => {
    setWsStatus(websocketService.getConnectionState());
    websocketService.on('device_update', handleDeviceUpdate);
    websocketService.on('device_presence_changed', handleDeviceUpdate);
    websocketService.on('connected', handleWsConnected);
    websocketService.on('disconnected', handleWsDisconnected);

    return () => {
      websocketService.off('device_update', handleDeviceUpdate);
      websocketService.off('device_presence_changed', handleDeviceUpdate);
      websocketService.off('connected', handleWsConnected);
      websocketService.off('disconnected', handleWsDisconnected);
    };
  }, [handleDeviceUpdate, handleWsConnected, handleWsDisconnected]);

  const loadUnits = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) setLoading(true);
    try {
      const res = await unitAPI.getUnits();
      if (res.data && res.data.success) {
        const polledUnits = res.data.data || [];
        setUnits((currentUnits) => mergePolledUnits(currentUnits, polledUnits));
        websocketService.syncDeviceSubscriptions(polledUnits.map((unit) => unit.deviceId));
      }
    } catch (error) {
      console.error('Failed to load units:', error);
      if (!silent) setUnits([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUnits();
    return () => websocketService.syncDeviceSubscriptions([]);
  }, [loadUnits]);

  useEffect(() => {
    const refreshMs = Number.isFinite(UNIT_STATUS_REFRESH_MS)
      ? Math.max(UNIT_STATUS_REFRESH_MS, 10000)
      : 60000;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        loadUnits({ silent: true });
      }
    }, refreshMs);

    return () => clearInterval(interval);
  }, [loadUnits]);

  const parseImportRows = (rawText) => rawText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line, index) => !(index === 0 && (/^device\s*no/i.test(line) || /^deviceNo/i.test(line))))
    .map((line) => {
      const [deviceNo, password] = line.split(/[,;\t]/).map((item) => item.trim());
      return { deviceNo, password: password || undefined };
    })
    .filter((row) => row.deviceNo);

  const downloadImportTemplate = () => {
    const csv = 'deviceNo,password\n89860831112390088542,pudow\n89860831112390088543,pudow\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'atmwater-device-import-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRegisterDevice = async () => {
    if (!canEditUnits) return;
    const rows = parseImportRows(importDeviceNo);
    if (rows.length === 0) {
      setImportMessage({ type: 'error', text: t('unitControl.enterDeviceNo') });
      return;
    }

    setImportLoading(true);
    setImportMessage(null);

    const result = { success: 0, exists: 0, failed: [] };
    for (const row of rows) {
      try {
        const res = await unitAPI.registerDevice(row);
        if (res.data?.success && res.data.alreadyExists) {
          result.exists += 1;
        } else if (res.data?.success) {
          result.success += 1;
        } else {
          result.failed.push(`${row.deviceNo}: ${res.data?.message || t('unitControl.importFailed')}`);
        }
      } catch (error) {
        console.error('Failed to register device:', error);
        const message = error.response?.status === 404
          ? t('unitControl.deviceNotFound')
          : (error.response?.data?.message || t('unitControl.importError'));
        result.failed.push(`${row.deviceNo}: ${message}`);
      }
    }

    const summary = t('unitControl.importSummary', {
      success: result.success,
      exists: result.exists,
      failed: result.failed.length
    });
    const failedText = result.failed.length > 0
      ? ` ${t('unitControl.importFailedItems', { items: result.failed.slice(0, 3).join('; ') })}`
      : '';
    setImportMessage({
      type: result.failed.length > 0 ? 'error' : (result.exists > 0 ? 'info' : 'success'),
      text: `${summary}${failedText}`
    });
    if (result.failed.length === 0) {
      setImportDeviceNo('');
    }
    await loadUnits();
    setImportLoading(false);
  };

  // 鎵撳紑缂栬緫寮圭獥
  const handleEditUnit = (unit) => {
    if (!canOpenUnitEditor) return;
    const trueInstalledAt = unit.installationSource === 'installed_at'
      ? formatDateInput(unit.installedAt)
      : '';

    setSelectedUnitForEdit(unit);
    setEditForm({
      rpOwnerId: unit.rpOwnerId || '',
      puskudId: unit.puskudId || '',
      stewardId: unit.stewardId || '',
      installedAt: trueInstalledAt,
      simCardNumber: unit.simCardNumber || '',
      simPaidUntil: formatDateInput(unit.simPaidUntil),
      location: unit.location || '',
      provinceCode: unit.provinceCode || '',
      provinceName: unit.provinceName || '',
      cityName: unit.cityName || '',
      districtName: unit.districtName || '',
      addressDetail: unit.addressDetail || '',
      mineralWaterPrice: unit.mineralWaterPrice || '',
      pureWaterPrice: unit.pureWaterPrice || ''
    });
    setShowEditUnitDialog(true);
  };

  const openWaterExport = (unit) => {
    setExportUnit(unit);
    setExportError('');
  };

  const closeUnitQr = () => {
    if (qrLoading) return;
    setQrUnit(null);
    setQrData(null);
    setQrError('');
  };

  const openUnitQr = async (unit) => {
    if (!canViewUnitQr || !unit?.deviceId) return;
    setQrUnit(unit);
    setQrData(null);
    setQrError('');
    setQrLoading(true);
    try {
      const response = await unitAPI.getDeviceQR(unit.deviceId);
      if (!response?.data?.success || !response.data.data) {
        throw new Error('INVALID_UNIT_QR_RESPONSE');
      }
      setQrData(buildUnitOutletQrCodes({
        requestedDeviceId: unit.deviceId,
        backendData: response.data.data,
        apiBaseUrl: API_BASE_URL
      }));
    } catch (error) {
      console.error('[Unit QR] Failed to load environment-confirmed QR data:', error);
      setQrError(error?.response?.data?.message || t('unitControl.qrGenerateFailed'));
    } finally {
      setQrLoading(false);
    }
  };

  const downloadUnitQr = (svgId, filename, footerLines) => {
    const svg = document.getElementById(svgId);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 480;
    canvas.height = 560;
    const context = canvas.getContext('2d');
    const image = new Image();
    image.onload = () => {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 40, 20, 400, 400);
      context.fillStyle = '#111827';
      context.textAlign = 'center';
      context.font = 'bold 20px Courier New, Courier, monospace';
      footerLines.forEach((line, index) => context.fillText(line, 240, 465 + (index * 30)));
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    image.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  const handleWaterExport = async () => {
    if (!exportUnit || exportLoading) return;
    setExportLoading(true);
    setExportError('');
    try {
      const response = await unitAPI.getWaterExportData(exportUnit.id, exportYear, exportMonth);
      const payload = response?.data?.data;
      if (!response?.data?.success || !payload) {
        throw new Error(response?.data?.message || 'Invalid export response');
      }
      downloadUnitWaterWorkbook(payload, t);
      setExportUnit(null);
    } catch (error) {
      console.error('Failed to export unit water data:', error);
      setExportError(error?.response?.data?.message || t('units.export.error'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleApplyAddressSplit = (address = editForm.location) => {
    const parsed = splitIndonesianAddress(address);
    setEditForm((form) => ({
      ...form,
      provinceCode: '',
      provinceName: parsed.provinceName,
      cityName: parsed.cityName,
      districtName: parsed.districtName,
      addressDetail: parsed.addressDetail
    }));
  };

  // 淇濆瓨缂栬緫
  const handleSaveUnitDetails = async () => {
    if (!selectedUnitForEdit) return;

    const updatePayload = {
      location: editForm.location,
      provinceCode: editForm.provinceCode || null,
      provinceName: editForm.provinceName || null,
      cityName: editForm.cityName || null,
      districtName: editForm.districtName || null,
      addressDetail: editForm.addressDetail || null
    };
    Object.assign(updatePayload, buildUnitPartyBindingPayload(editForm, canManagePartnerBindings));
    const originalInstalledAt = selectedUnitForEdit.installationSource === 'installed_at'
      ? formatDateInput(selectedUnitForEdit.installedAt)
      : '';
    if (editForm.installedAt !== originalInstalledAt) {
      updatePayload.installedAt = editForm.installedAt || null;
    }
    const originalSimCardNumber = String(selectedUnitForEdit.simCardNumber || '').trim();
    const nextSimCardNumber = String(editForm.simCardNumber || '').trim();
    if (nextSimCardNumber !== originalSimCardNumber) {
      updatePayload.simCardNumber = nextSimCardNumber || null;
    }
    const originalSimPaidUntil = formatDateInput(selectedUnitForEdit.simPaidUntil);
    if (editForm.simPaidUntil !== originalSimPaidUntil) {
      updatePayload.simPaidUntil = editForm.simPaidUntil || null;
    }

    setEditLoading(true);
    try {
      const res = await unitAPI.updateUnitDetail(selectedUnitForEdit.id, updatePayload);
      if (res.data?.success) {
        alert(res.data?.message || t('unitControl.updateSuccess') || 'Unit updated successfully');
        setShowEditUnitDialog(false);
        loadUnits();
      }
    } catch (error) {
      const responseData = error.response?.data || {};
      if (responseData.code === 'UNIT_INSTALLATION_SCHEMA_PENDING') {
        alert(t('unitControl.installedAtSchemaPending', {
          defaultValue: 'Installation date cannot be saved yet. Ask backend to run the approved unit installation migration on dev first.'
        }));
      } else if (responseData.code === 'UNIT_SIM_SCHEMA_PENDING') {
        alert(t('unitControl.simInfoSchemaPending', {
          defaultValue: 'SIM card number and payment date cannot be saved yet. Ask backend to run the approved unit SIM info migration on dev first.'
        }));
      } else {
        alert(responseData.message || t('unitControl.updateFailed', {
          defaultValue: `Failed to update unit: ${error.message}`
        }));
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveUnitPricing = async () => {
    if (!selectedUnitForEdit) return;

    const mineralPriceError = validateUnitPrice(editForm.mineralWaterPrice, t('units.table.mineralWater'), MINERAL_WATER_PRICE_LIMITS);
    if (mineralPriceError) {
      alert(mineralPriceError);
      return;
    }

    const purePriceError = validateUnitPrice(editForm.pureWaterPrice, t('units.table.pureWater'), PURE_WATER_PRICE_LIMITS);
    if (purePriceError) {
      alert(purePriceError);
      return;
    }

    const updatePayload = {};
    if (normalizePriceInput(editForm.mineralWaterPrice) !== normalizePriceInput(selectedUnitForEdit.mineralWaterPrice)) {
      updatePayload.mineralWaterPrice = editForm.mineralWaterPrice;
    }
    if (normalizePriceInput(editForm.pureWaterPrice) !== normalizePriceInput(selectedUnitForEdit.pureWaterPrice)) {
      updatePayload.pureWaterPrice = editForm.pureWaterPrice;
    }
    if (Object.keys(updatePayload).length === 0) {
      alert(t('unitControl.noPricingChanges', { defaultValue: 'No water-price changes to save.' }));
      return;
    }

    setEditLoading(true);
    try {
      const res = await unitAPI.updateUnitPricing(selectedUnitForEdit.id, updatePayload);
      if (res.data?.success) {
        alert(res.data?.message || t('unitControl.pricingUpdateSuccess', { defaultValue: 'Water pricing updated successfully.' }));
        setSelectedUnitForEdit((unit) => unit ? {
          ...unit,
          mineralWaterPrice: updatePayload.mineralWaterPrice ?? unit.mineralWaterPrice,
          pureWaterPrice: updatePayload.pureWaterPrice ?? unit.pureWaterPrice
        } : unit);
        loadUnits();
      }
    } catch (error) {
      alert(error.response?.data?.message || t('unitControl.updateFailed', {
        defaultValue: `Failed to update unit: ${error.message}`
      }));
    } finally {
      setEditLoading(false);
    }
  };

  const partnerFilterOptions = Array.from(new Set(
    units.map((unit) => getUnitPartner(unit)?.name).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));
  const regionalPartnerFilterOptions = Array.from(new Set(
    units.map((unit) => getUnitRegionalPartner(unit)?.name).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));

  const filteredUnits = units.filter(u => {
    const search = searchTerm.trim().toLowerCase();
    const partnerName = getUnitPartner(u)?.name || '';
    const regionalPartnerName = getUnitRegionalPartner(u)?.name || '';
    const matchesSearch = !search || buildUnitSearchText(u).includes(search);
    const matchesPartner = partnerFilter === 'all' || partnerName === partnerFilter;
    const matchesRegionalPartner = regionalPartnerFilter === 'all' || regionalPartnerName === regionalPartnerFilter;
    return matchesSearch && matchesPartner && matchesRegionalPartner;
  });

  const renderUnitStatusCell = (unit) => {
    const connectionStatus = getUnitConnectionStatus(unit);
    const openAlertCount = getUnitOpenAlertCount(unit);
    const latestOpenAlert = getUnitLatestOpenAlert(unit);
    const warning = hasUnitOpenAlert(unit);
    const connectionOnline = connectionStatus === 'Online';
    const connectionUnknown = connectionStatus === 'Unknown' || connectionStatus === 'StagingNotConnected';
    const connectionPillClass = connectionOnline
      ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
      : connectionUnknown
        ? 'bg-slate-500/10 text-slate-300 border border-slate-500/30'
        : 'bg-red-500/10 text-red-400 border border-red-500/30';
    const connectionDotClass = connectionOnline
      ? 'bg-emerald-400'
      : connectionUnknown ? 'bg-slate-400' : 'bg-red-400';
    const pillClass = warning
      ? 'bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20'
      : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30';
    const dotClass = warning ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400';
    const label = warning
      ? t('units.status.Warning', { defaultValue: 'Warning' })
      : t('units.status.Normal', { defaultValue: 'Normal' });
    const alertHref = latestOpenAlert?.id
      ? `/device-alerts?status=open&unitId=${unit.id}&alertId=${latestOpenAlert.id}`
      : `/device-alerts?status=open&unitId=${unit.id}`;
    const statusPill = (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${pillClass}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></span>
        {label}
        {warning && openAlertCount > 0 && <span className="font-mono">({openAlertCount})</span>}
      </span>
    );

    return (
      <div className="space-y-2">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${connectionPillClass}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${connectionDotClass}`}></span>
          {t(`units.status.${connectionStatus}`, { defaultValue: connectionStatus })}
        </span>
        {warning ? (
          <Link
            to={alertHref}
            className="inline-flex"
            title={t('unitControl.openAlertDetail', { defaultValue: 'Open alert detail' })}
          >
            {statusPill}
          </Link>
        ) : statusPill}
        {warning && latestOpenAlert && (
          <div className="max-w-[180px] truncate text-[10px] text-amber-200" title={latestOpenAlert.alertMessage || latestOpenAlert.errorCode || latestOpenAlert.alertType}>
            {latestOpenAlert.errorCode || latestOpenAlert.alertType || t('unitControl.activeAlert', { defaultValue: 'Active alert' })}
          </div>
        )}
        <div className="text-[10px] text-slate-500">
          {t('unitControl.connectionLabel', { defaultValue: 'Connection' })}: {t(`units.status.${connectionStatus}`, { defaultValue: connectionStatus })}
        </div>
        {unit.stagingRuntimeIsolated && (
          <div className="max-w-[180px] text-[10px] text-amber-500">
            {t('unitControl.stagingIsolationHint', { defaultValue: 'Staging does not accept device TCP connections.' })}
          </div>
        )}
        <div className="text-[10px] text-slate-500">
          {t('unitControl.lastDataLabel', { defaultValue: 'Last data' })}: {formatLastHeartbeat(getPresenceLastDataAt(unit))}
        </div>
        <div className="text-[10px] text-slate-500">
          {t('unitControl.lastDisconnectedLabel', { defaultValue: 'Disconnected' })}: {formatLastHeartbeat(unit.lastDisconnectedAt)}
        </div>
        <div className="max-w-[180px] truncate text-[10px] text-slate-500" title={unit.connectionDisplaySource || unit.statusSource || 'unknown'}>
          {t('unitControl.statusSourceLabel', { defaultValue: 'Source' })}: {t(`unitControl.statusSources.${unit.connectionDisplaySource || unit.statusSource || 'unknown'}`, { defaultValue: unit.connectionDisplaySource || unit.statusSource || 'unknown' })}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-8 space-y-8">
      <header className="flex flex-col gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white flex items-center gap-3 whitespace-nowrap">
            <Monitor className="text-cyan-400" size={32} />
            {t('units.title')}
          </h2>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-slate-400 text-sm">
              <Trans i18nKey="units.totalUnits" count={units.length}>
                Total <span className="text-cyan-400 font-bold">{{count: units.length}}</span> Units Monitoring
              </Trans>
            </p>
            {/* WebSocket杩炴帴鐘舵€佹寚绀?*/}
            {wsStatus === 'connected' ? (
              <span className="flex items-center gap-1.5 text-xs text-emerald-500">
                <Wifi size={12} />
                {t('unitControl.wsOnline')}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs text-amber-400" role="status">
                <WifiOff size={12} />
                {t('unitControl.wsReconnecting', { defaultValue: 'Realtime reconnecting; polling remains active' })}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text"
              placeholder={t('units.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchProps(e.target.value)}
              className="bg-slate-800 border border-white/10 rounded-lg py-2 pl-10 pr-4 text-sm text-white focus:border-cyan-400 outline-none w-full sm:w-80"
            />
          </div>
          <select
            value={partnerFilter}
            onChange={(event) => setPartnerFilter(event.target.value)}
            className="bg-slate-800 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:border-cyan-400 outline-none"
          >
            <option value="all">{t('unitControl.filters.allPartners')}</option>
            {partnerFilterOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <select
            value={regionalPartnerFilter}
            onChange={(event) => setRegionalPartnerFilter(event.target.value)}
            className="bg-slate-800 border border-white/10 rounded-lg py-2 px-3 text-sm text-white focus:border-cyan-400 outline-none"
          >
            <option value="all">{t('unitControl.filters.allRegionalPartners')}</option>
            {regionalPartnerFilterOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          {canEditUnits && (
            <button
              onClick={() => setShowImportDialog(true)}
              className="bg-slate-700 hover:bg-slate-600 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all"
            >
              <Upload size={16} />
              {t('unitControl.importDevice')}
            </button>
          )}
          <span className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-400">
            <Wifi size={14} className="text-cyan-400" />
            {t('unitControl.autoRefresh')}
          </span>
        </div>
      </header>

      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-slate-800/50">
        <table className="min-w-[1080px] w-full text-left border-collapse">
          <thead className="bg-slate-900/50 text-xs uppercase text-slate-500 font-bold">
            <tr>
              <th className="p-4 border-b border-white/5">{t('units.table.unitId')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.partner')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.regionalPartner')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.steward')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.waterPricing')}</th>
              <th className="p-4 border-b border-white/5">{t('units.table.status')}</th>
              <th className="p-4 border-b border-white/5">{t('unitControl.inspection.column')}</th>
              <th className="p-4 border-b border-white/5 text-right">{t('units.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading && units.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-12 text-center text-slate-500">{t('units.table.loading')}</td>
              </tr>
            ) : filteredUnits.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-12 text-center text-slate-500 text-lg">
                  <AlertTriangle className="mx-auto mb-2 opacity-20" size={48} />
                  {t('units.table.noUnits')}
                </td>
              </tr>
            ) : filteredUnits.map((unit) => (
              <tr
                key={unit.id}
                className={`border-b border-white/5 hover:bg-white/5 transition-all group ${
                  unit._justUpdated ? 'bg-cyan-500/10' : ''
                }`}
              >
                <td className="p-4">
                   <Link
                    to={`/units/${unit.id}`}
                    className="font-mono font-bold text-cyan-400 hover:underline"
                    title={unit.deviceId ? `Full device ID: ${unit.deviceId}` : undefined}
                  >
                    {formatStationId(unit)}
                  </Link>
                  {formatListDeviceName(unit) && (
                    <Link
                      to={`/units/${unit.id}`}
                      className="mt-2 block text-xs font-medium text-white transition-colors hover:text-cyan-200 hover:underline"
                    >
                      {formatListDeviceName(unit)}
                    </Link>
                  )}
                  {formatCompactStationAddress(unit) && (
                    <div
                      className="mt-1 flex max-w-[220px] items-center gap-1 text-[10px] leading-4 text-slate-500"
                      title={getFullStationAddress(unit)}
                    >
                      <MapPin size={11} className="shrink-0" />
                      <span className="truncate">{formatCompactStationAddress(unit)}</span>
                    </div>
                  )}
                </td>
                <td className="p-4">{renderPartyCell(getUnitPartner(unit))}</td>
                <td className="p-4">{renderPartyCell(getUnitRegionalPartner(unit))}</td>
                <td className="p-4">{renderPartyCell(getUnitSteward(unit))}</td>
                <td className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">
                        {t('units.table.mineralWater')}
                      </span>
                      <span className="text-xs font-bold text-cyan-300">
                        {formatWaterPrice(unit.mineralWaterPrice)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-2">
                      <span className="text-[10px] uppercase tracking-wide text-slate-500">
                        {t('units.table.pureWater')}
                      </span>
                      <span className="text-xs font-bold text-emerald-300">
                        {formatWaterPrice(unit.pureWaterPrice)}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  {renderUnitStatusCell(unit)}
                </td>
                <td className="p-4">
                  <div className="space-y-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold ${
                      unit.inspectedToday
                        ? 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                        : 'border border-slate-600 bg-slate-700/60 text-slate-300'
                    }`}>
                      {unit.inspectedToday ? t('unitControl.inspection.checkedToday') : t('unitControl.inspection.notCheckedToday')}
                    </span>
                    <div className="text-[11px] font-medium text-slate-300">
                      {t('unitControl.inspection.latest')}: {formatInspectionDateTime(unit.latestInspectionAt, i18n.language) || t('unitControl.inspection.none')}
                    </div>
                    <div className="text-[11px] font-medium text-slate-300">
                      {t('unitControl.inspection.reviewStatus')}: {unit.latestInspectionReviewStatus
                        ? t(`unitControl.inspection.review.${unit.latestInspectionReviewStatus}`, { defaultValue: unit.latestInspectionReviewStatus })
                        : t('unitControl.inspection.none')}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <div className="flex min-w-[48px] items-center justify-end gap-1">
                    {canExportUnitWater && <button
                      onClick={() => openWaterExport(unit)}
                      className="p-1.5 text-slate-500 hover:text-emerald-400 transition-colors"
                      title={t('units.export.action')}
                      aria-label={`${t('units.export.action')} ${formatStationId(unit)}`}
                    >
                      <FileSpreadsheet size={16} />
                    </button>}
                  {canViewUnitQr && <button
                    onClick={() => openUnitQr(unit)}
                    className="p-1.5 text-slate-500 hover:text-cyan-400 transition-colors"
                    title={t('unitControl.generateQrAction')}
                    aria-label={`${t('unitControl.generateQrAction')} ${formatStationId(unit)}`}
                  >
                    <QrCode size={16} />
                  </button>}
                  {canOpenUnitEditor && <button
                    onClick={() => handleEditUnit(unit)}
                    className="p-1.5 text-slate-500 hover:text-orange-400 transition-colors"
                    title={t('unitControl.editUnitAction')}
                  >
                    <Edit size={16} />
                  </button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canViewUnitQr && qrUnit && (
        <AdminModalSurface onClose={closeUnitQr}>
          <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-slate-800 p-6 shadow-2xl sm:p-8">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="flex items-center gap-2 text-xl font-bold text-white">
                  <QrCode size={24} className="text-cyan-400" />
                  {t('unitControl.qrCodeTitle')}
                </h3>
                <p className="mt-2 break-all font-mono text-xs text-cyan-300">{qrUnit.deviceId}</p>
              </div>
              <button
                onClick={closeUnitQr}
                disabled={qrLoading}
                className="text-slate-400 transition-colors hover:text-white disabled:opacity-40"
                aria-label={t('common.close', { defaultValue: 'Close' })}
              >
                <X size={22} />
              </button>
            </div>

            {qrLoading ? (
              <div className="flex items-center justify-center gap-3 py-16 text-sm font-semibold text-slate-300">
                <Loader2 size={24} className="animate-spin text-cyan-400" />
                {t('unitControl.qrConfirmingEnvironment')}
              </div>
            ) : qrError ? (
              <div role="alert" className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-5 py-6 text-center text-sm font-semibold text-rose-300">
                {qrError}
              </div>
            ) : qrData ? (
              <>
                <div className={`mb-5 rounded-2xl border px-4 py-3 ${
                  qrData.environment === 'development'
                    ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
                    : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                }`}>
                  <p className="text-sm font-black uppercase tracking-wide">
                    {t(`unitControl.qrEnvironment.${qrData.environment}`)}
                  </p>
                  <p className="mt-1 text-xs leading-5">
                    {t('unitControl.qrEnvironmentWarning', {
                      environment: t(`unitControl.qrEnvironment.${qrData.environment}`)
                    })}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {[
                    { key: 'mineral', label: t('unitControl.qrLabels.mineral') },
                    { key: 'pure', label: t('unitControl.qrLabels.pure') }
                  ].map(({ key, label }) => {
                    const outlet = qrData[key];
                    const svgId = `unit-qr-${key}`;
                    const outletIdentity = `${qrData.deviceId}-${outlet.outletNo}`;
                    return (
                      <div key={key} className="flex flex-col items-center rounded-2xl border border-white/10 bg-slate-900/50 p-5">
                        <p className="text-base font-black text-white">{label}</p>
                        <div className="mt-4 rounded-2xl bg-white p-3">
                          <QRCodeSVG id={svgId} value={outlet.url} size={190} level="H" />
                        </div>
                        <p className="mt-3 break-all text-center font-mono text-xs text-cyan-300">{outletIdentity}</p>
                        <button
                          type="button"
                          onClick={() => downloadUnitQr(
                            svgId,
                            `ATMWater_${qrData.environment}_${key}_${qrData.deviceId}`,
                            [outletIdentity, t(`unitControl.qrEnvironment.${qrData.environment}`)]
                          )}
                          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-4 py-2.5 text-sm font-black text-slate-950 transition-colors hover:bg-cyan-300"
                        >
                          <Download size={16} />
                          {t('common.download')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        </AdminModalSurface>
      )}

      {canExportUnitWater && exportUnit && (
        <AdminModalSurface onClose={() => !exportLoading && setExportUnit(null)}>
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-slate-800 p-7 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h3 className="flex items-center gap-2 text-xl font-bold text-white">
                  <FileSpreadsheet size={22} className="text-emerald-400" />
                  {t('units.export.title')}
                </h3>
                <p className="mt-2 break-all text-xs text-slate-400">{exportUnit.deviceId || formatStationId(exportUnit)}</p>
              </div>
              <button
                onClick={() => setExportUnit(null)}
                disabled={exportLoading}
                className="text-slate-400 transition-colors hover:text-white disabled:opacity-40"
                aria-label={t('common.close', { defaultValue: 'Close' })}
              >
                <X size={22} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs font-bold text-slate-300">
                {t('units.export.year')}
                <select
                  value={exportYear}
                  onChange={(event) => setExportYear(Number(event.target.value))}
                  className="mt-2 w-full rounded-xl border border-white/10 bg-slate-700 px-3 py-3 text-sm text-white outline-none focus:border-cyan-400"
                >
                  {Array.from({ length: Math.max(1, jakartaNow.getUTCFullYear() - 2019) }, (_, index) => jakartaNow.getUTCFullYear() - index).map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-bold text-slate-300">
                {t('units.export.month')}
                <select
                  value={exportMonth}
                  onChange={(event) => setExportMonth(Number(event.target.value))}
                  className="mt-2 w-full rounded-xl border border-white/10 bg-slate-700 px-3 py-3 text-sm text-white outline-none focus:border-cyan-400"
                >
                  {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                    <option key={month} value={month}>{t('units.export.monthOption', { month })}</option>
                  ))}
                </select>
              </label>
            </div>
            <p className="mt-4 rounded-xl border border-cyan-500/20 bg-cyan-500/5 px-4 py-3 text-xs leading-5 text-slate-300">
              {t('units.export.description')}
            </p>
            {exportError && <p role="alert" className="mt-3 text-sm font-semibold text-rose-400">{exportError}</p>}
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setExportUnit(null)}
                disabled={exportLoading}
                className="flex-1 rounded-xl bg-slate-700 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-600 disabled:opacity-40"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleWaterExport}
                disabled={exportLoading}
                className="flex-1 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-bold text-slate-950 transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex items-center justify-center gap-2">
                  {exportLoading ? <Loader2 size={17} className="animate-spin" /> : <FileSpreadsheet size={17} />}
                  {exportLoading ? t('units.export.exporting') : t('units.export.download')}
                </span>
              </button>
            </div>
          </div>
        </AdminModalSurface>
      )}

      {/* Import Device Dialog */}
      {canEditUnits && showImportDialog && (
        <AdminModalSurface onClose={() => setShowImportDialog(false)}>
          <div className="bg-slate-800 rounded-3xl p-8 w-full max-w-xl border border-white/10 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Upload size={24} className="text-cyan-400" />
                {t('unitControl.importTitle')}
              </h3>
              <button
                onClick={() => {
                  setShowImportDialog(false);
                  setImportDeviceNo('');
                  setImportMessage(null);
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-300 mb-2">
                  {t('unitControl.batchDeviceNo')}
                </label>
                <textarea
                  value={importDeviceNo}
                  onChange={(e) => setImportDeviceNo(e.target.value)}
                  placeholder={t('unitControl.batchDeviceNoPlaceholder')}
                  className="min-h-[140px] w-full resize-y bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50"
                  disabled={importLoading}
                  autoFocus
                />
              </div>

              {/* Import Message */}
              {importMessage && (
                <div className={`flex items-start gap-3 p-4 rounded-xl ${
                  importMessage.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30' :
                  importMessage.type === 'error' ? 'bg-rose-500/10 border border-rose-500/30' :
                  'bg-amber-500/10 border border-amber-500/30'
                }`}>
                  {importMessage.type === 'success' ? (
                    <CheckCircle2 size={20} className="text-emerald-400 shrink-0 mt-0.5" />
                  ) : importMessage.type === 'error' ? (
                    <AlertTriangle size={20} className="text-rose-400 shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                  )}
                  <p className={`text-sm font-semibold ${
                    importMessage.type === 'success' ? 'text-emerald-300' :
                    importMessage.type === 'error' ? 'text-rose-300' :
                    'text-amber-300'
                  }`}>
                    {importMessage.text}
                  </p>
                </div>
              )}

              {/* Info */}
              <div className="flex flex-col gap-3 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl sm:flex-row sm:items-start sm:justify-between">
                <p className="text-xs text-cyan-300 leading-relaxed">
                  {t('unitControl.importInfo')}
                </p>
                <button
                  type="button"
                  onClick={downloadImportTemplate}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-cyan-400/40 px-3 py-2 text-xs font-bold text-cyan-200 hover:bg-cyan-400/10 transition-colors"
                >
                  <Upload size={14} />
                  {t('unitControl.downloadTemplate')}
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportDeviceNo('');
                    setImportMessage(null);
                  }}
                  disabled={importLoading}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-700/50 text-white font-bold py-3 px-4 rounded-xl transition-all"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleRegisterDevice}
                  disabled={importLoading || !importDeviceNo.trim()}
                  className={`flex-1 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    importLoading || !importDeviceNo.trim()
                      ? 'bg-white/10 text-slate-500 cursor-not-allowed'
                      : 'bg-cyan-400 text-slate-900 hover:bg-cyan-300'
                  }`}
                >
                  {importLoading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t('unitControl.importing')}
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      {t('unitControl.importBtn')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </AdminModalSurface>
      )}
      {/* Edit Unit Dialog */}
      {canOpenUnitEditor && showEditUnitDialog && selectedUnitForEdit && (
        <AdminModalSurface onClose={() => setShowEditUnitDialog(false)}>
          <div className="bg-slate-800 rounded-3xl p-8 w-full max-w-4xl max-h-[92vh] overflow-y-auto border border-white/10 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Edit size={24} className="text-orange-400" />
                {t('unitControl.editUnitTitle')} <span className="font-mono text-cyan-400 text-sm" title={selectedUnitForEdit.deviceId}>{formatStationId(selectedUnitForEdit)}</span>
              </h3>
              <button
                onClick={() => setShowEditUnitDialog(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {canEditUnits && <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-1 mb-2">
                    <CalendarDays size={12} />
                    {t('unitControl.installedAtLabel', { defaultValue: 'Real install date' })}
                  </label>
                  <input
                    type="date"
                    value={editForm.installedAt}
                    onChange={(e) => setEditForm(f => ({ ...f, installedAt: e.target.value }))}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    title={t('unitControl.installedAtTitle', { defaultValue: 'Date when the device was actually installed at the customer site' })}
                  />
                </div>

                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-1 mb-2">
                    <MapPin size={12} />
                    {t('unitControl.fullAddressLabel', { defaultValue: 'Full station address' })}
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <textarea
                      rows={3}
                      value={editForm.location}
                      onChange={(e) => setEditForm(f => ({ ...f, location: e.target.value }))}
                      onBlur={() => handleApplyAddressSplit()}
                      placeholder={t('unitControl.fullAddressPlaceholder', { defaultValue: 'Paste the full Indonesian address here' })}
                      className="min-h-[88px] flex-1 resize-none bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleApplyAddressSplit()}
                      className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 text-sm font-bold text-cyan-200 hover:bg-cyan-400/20 sm:h-auto"
                      title={t('unitControl.splitAddressTitle', { defaultValue: 'Split full address into province, city, district, and detail fields' })}
                    >
                      <Wand2 size={16} />
                      {t('unitControl.splitAddressAction', { defaultValue: 'Split' })}
                    </button>
                  </div>
                </div>
              </div>

              {canManagePartnerBindings && <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-1 mb-2">
                    <Monitor size={12} />
                    {t('unitControl.simCardNumberLabel', { defaultValue: 'SIM card number' })}
                  </label>
                  <input
                    type="text"
                    value={editForm.simCardNumber}
                    onChange={(e) => setEditForm(f => ({ ...f, simCardNumber: e.target.value }))}
                    placeholder={t('unitControl.simCardNumberPlaceholder', { defaultValue: 'ICCID, SIM number, or phone number' })}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    title={t('unitControl.simCardNumberTitle', { defaultValue: 'SIM card number used by the device modem' })}
                  />
                </div>

                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-1 mb-2">
                    <CalendarDays size={12} />
                    {t('unitControl.simPaidUntilLabel', { defaultValue: 'SIM paid until' })}
                  </label>
                  <input
                    type="date"
                    value={editForm.simPaidUntil}
                    onChange={(e) => setEditForm(f => ({ ...f, simPaidUntil: e.target.value }))}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                    title={t('unitControl.simPaidUntilTitle', { defaultValue: 'Date until which the SIM service is paid and expected to stay active' })}
                  />
                </div>
              </div>

              </>}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                    {t('unitControl.provinceNameLabel', { defaultValue: 'Province' })}
                  </label>
                  <input
                    type="text"
                    value={editForm.provinceName}
                    onChange={(e) => setEditForm(f => ({ ...f, provinceCode: '', provinceName: e.target.value }))}
                    placeholder={t('unitControl.locationPlaceholders.province', { defaultValue: 'Province' })}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                    {t('unitControl.cityNameLabel', { defaultValue: 'City / regency' })}
                  </label>
                  <input
                    type="text"
                    value={editForm.cityName}
                    onChange={(e) => setEditForm(f => ({ ...f, cityName: e.target.value }))}
                    placeholder={t('unitControl.locationPlaceholders.city', { defaultValue: 'City or regency' })}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                    {t('unitControl.districtNameLabel', { defaultValue: 'District' })}
                  </label>
                  <input
                    type="text"
                    value={editForm.districtName}
                    onChange={(e) => setEditForm(f => ({ ...f, districtName: e.target.value }))}
                    placeholder={t('unitControl.locationPlaceholders.district', { defaultValue: 'District' })}
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">
                  {t('unitControl.addressDetailLabel', { defaultValue: 'Address detail' })}
                </label>
                <input
                  type="text"
                  value={editForm.addressDetail}
                  onChange={(e) => setEditForm(f => ({ ...f, addressDetail: e.target.value }))}
                  placeholder={t('unitControl.locationPlaceholders.addressDetail', { defaultValue: 'Street, building, unit number' })}
                  className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                />
              </div>

              {canManagePartnerBindings && <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <RoleUserSearchField
                  fieldId="unit-kud-user-search"
                  label={t('unitControl.kudBinding')}
                  placeholder={t('unitControl.searchKudPlaceholder')}
                  roles={['KUD']}
                  selectedId={editForm.rpOwnerId}
                  initialQuery={formatPartySearchQuery(getUnitPartner(selectedUnitForEdit), selectedUnitForEdit.rpOwnerId)}
                  resetKey={`${selectedUnitForEdit.id}:kud`}
                  onSelect={(id) => setEditForm((form) => ({ ...form, rpOwnerId: id }))}
                  onClear={() => setEditForm((form) => ({ ...form, rpOwnerId: '' }))}
                />
                <RoleUserSearchField
                  fieldId="unit-puskud-user-search"
                  label={t('unitControl.puskudBinding')}
                  placeholder={t('unitControl.searchPuskudPlaceholder')}
                  roles={['PUSKUD']}
                  selectedId={editForm.puskudId}
                  initialQuery={formatPartySearchQuery(getUnitRegionalPartner(selectedUnitForEdit), selectedUnitForEdit.puskudId)}
                  resetKey={`${selectedUnitForEdit.id}:puskud`}
                  onSelect={(id) => setEditForm((form) => ({ ...form, puskudId: id }))}
                  onClear={() => setEditForm((form) => ({ ...form, puskudId: '' }))}
                />
              </div>
              <RoleUserSearchField
                fieldId="unit-steward-user-search"
                label={t('unitControl.stewardBinding')}
                placeholder={t('unitControl.searchStewardPlaceholder')}
                roles={['Steward']}
                selectedId={editForm.stewardId}
                initialQuery={formatPartySearchQuery(getUnitSteward(selectedUnitForEdit), selectedUnitForEdit.stewardId)}
                resetKey={`${selectedUnitForEdit.id}:steward`}
                onSelect={(id) => setEditForm((form) => ({ ...form, stewardId: id }))}
                onClear={() => setEditForm((form) => ({ ...form, stewardId: '' }))}
              />
              </>}
              </>}

              {canManageUnitPricing && (
              <section className="space-y-4 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4">
                <div>
                  <h4 className="text-sm font-black text-amber-200">{t('unitControl.pricingSectionTitle', { defaultValue: 'Water pricing' })}</h4>
                  <p className="mt-1 text-xs text-amber-100/70">{t('unitControl.pricingSectionHint', { defaultValue: 'This is a separate high-risk permission. Saving prices does not change any other station data.' })}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">{t('unitControl.mineralWaterPriceLabel')}</label>
                  <input
                    type="number"
                    value={editForm.mineralWaterPrice}
                    onChange={(e) => setEditForm(f => ({ ...f, mineralWaterPrice: e.target.value }))}
                    placeholder={String(DEFAULT_WATER_PRICES.mineral)}
                    min={MINERAL_WATER_PRICE_LIMITS.min}
                    max={MINERAL_WATER_PRICE_LIMITS.max}
                    step="1"
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                  <p className="mt-2 text-xs text-slate-500">{t('unitControl.priceRangeHint', { min: MINERAL_WATER_PRICE_LIMITS.min, max: MINERAL_WATER_PRICE_LIMITS.max })}</p>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase font-black block mb-2">{t('unitControl.pureWaterPriceLabel')}</label>
                  <input
                    type="number"
                    value={editForm.pureWaterPrice}
                    onChange={(e) => setEditForm(f => ({ ...f, pureWaterPrice: e.target.value }))}
                    placeholder={String(DEFAULT_WATER_PRICES.pure)}
                    min={PURE_WATER_PRICE_LIMITS.min}
                    max={PURE_WATER_PRICE_LIMITS.max}
                    step="1"
                    className="w-full bg-slate-700/50 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  />
                  <p className="mt-2 text-xs text-slate-500">{t('unitControl.priceRangeHint', { min: PURE_WATER_PRICE_LIMITS.min, max: PURE_WATER_PRICE_LIMITS.max })}</p>
                </div>
                </div>
              </section>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEditUnitDialog(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 px-4 rounded-xl transition-all"
                >
                  {t('common.cancel')}
                </button>
                {canEditUnits && <button
                  onClick={handleSaveUnitDetails}
                  disabled={editLoading}
                  className={`flex-1 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    editLoading ? 'bg-white/10 text-slate-500 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-400'
                  }`}
                >
                  {editLoading ? <><Loader2 size={18} className="animate-spin" />{t('unitControl.savingChanges')}</> : <><Save size={18} />{t('unitControl.saveChanges')}</>}
                </button>}
                {canManageUnitPricing && <button
                  onClick={handleSaveUnitPricing}
                  disabled={editLoading}
                  className={`flex-1 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    editLoading ? 'bg-white/10 text-slate-500 cursor-not-allowed' : 'bg-amber-500 text-slate-950 hover:bg-amber-400'
                  }`}
                >
                  {editLoading ? <><Loader2 size={18} className="animate-spin" />{t('unitControl.savingChanges')}</> : <><Save size={18} />{t('unitControl.savePricing', { defaultValue: 'Save water pricing' })}</>}
                </button>}
              </div>
            </div>
          </div>
        </AdminModalSurface>
      )}
    </div>
  );
}


